import { CheckCircle2, Undo2, Pencil, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { GUEST_TYPE_BADGE } from "@/components/checkin/GuestFormModal";

function TypeBadge({ g, className = "" }) {
  return (
    <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${GUEST_TYPE_BADGE[g.guest_type] || GUEST_TYPE_BADGE.General} ${className}`}>
      {g.guest_type}
    </span>
  );
}

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : "");

function StatusBadge({ g }) {
  const inn = g.status === "CHECKED_IN";
  return (
    <span className={`inline-flex whitespace-nowrap px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${inn ? "bg-teal-500/20 text-teal-300 border border-teal-500/40" : "bg-slate-700/40 text-slate-300 border border-slate-600"}`} data-testid={`guest-status-${g.guest_id}`}>
      {inn ? "Checked In" : "Not Arrived"}
    </span>
  );
}

function GuestActions({ g, isAdmin, busy, onCheckIn, onUndo, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-end gap-1.5">
      {g.status !== "CHECKED_IN" ? (
        <Button size="sm" disabled={busy} onClick={() => onCheckIn(g)} className="bg-orange-500 hover:bg-orange-600 text-white font-bold" data-testid={`checkin-btn-${g.guest_id}`}>
          <CheckCircle2 size={14} className="mr-1" /> Check In
        </Button>
      ) : isAdmin && (
        <Button size="sm" variant="outline" disabled={busy} onClick={() => onUndo(g)} className="bg-slate-900 border-slate-600 text-slate-200" data-testid={`undo-btn-${g.guest_id}`}>
          <Undo2 size={14} className="mr-1" /> Undo
        </Button>
      )}
      {isAdmin && (
        <>
          <button onClick={() => onEdit(g)} className="p-2 rounded hover:bg-white/10 text-slate-400" data-testid={`edit-guest-${g.guest_id}`}><Pencil size={14} /></button>
          <button onClick={() => onDelete(g)} className="p-2 rounded hover:bg-rose-500/20 text-rose-400" data-testid={`delete-guest-${g.guest_id}`}><Trash2 size={14} /></button>
        </>
      )}
    </div>
  );
}

export function GuestTable({ items, ...actions }) {
  if (!items.length) return <div className="p-10 text-center text-slate-400" data-testid="guest-table-empty">No guests found.</div>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="guest-table">
        <thead className="text-[10px] uppercase tracking-[0.2em] text-slate-400 border-b border-slate-800">
          <tr>
            <th className="text-left p-3">Guest</th>
            <th className="text-left p-3 hidden 2xl:table-cell">Contact</th>
            <th className="text-left p-3 hidden sm:table-cell">Ticket Type</th>
            <th className="text-left p-3">Status</th>
            <th className="text-left p-3 hidden lg:table-cell">Checked In</th>
            <th className="p-3" />
          </tr>
        </thead>
        <tbody>
          {items.map((g) => (
            <tr key={g.id} className="border-b border-slate-800/70 hover:bg-white/[0.02]" data-testid={`guest-row-${g.guest_id}`}>
              <td className="p-3">
                <div className="font-semibold text-white">{g.full_name}</div>
                <div className="text-xs text-slate-500">{g.guest_id}</div>
                <div className="sm:hidden mt-1" data-testid={`guest-type-mobile-${g.guest_id}`}><TypeBadge g={g} /></div>
              </td>
              <td className="p-3 hidden 2xl:table-cell text-slate-300 text-xs"><div>{g.email}</div><div className="text-slate-500">{g.phone}</div></td>
              <td className="p-3 hidden sm:table-cell" data-testid={`guest-type-${g.guest_id}`}><TypeBadge g={g} /></td>
              <td className="p-3"><StatusBadge g={g} /></td>
              <td className="p-3 hidden lg:table-cell text-xs text-slate-400">{g.check_in_time && `${fmtTime(g.check_in_time)} · ${g.checked_in_by}`}</td>
              <td className="p-3"><GuestActions g={g} {...actions} /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
